// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import AddToCartButton, { IconConfig } from "@steiner/App/Components/Common/AddToCartButton";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { addToCartStatus } from "@steiner/XDL/constants";
import Cart from "@steiner/XDL/Icons/Cart";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = { addWishListToCart };

interface OwnProps {
    wishListId?: string;
    extendedStyles?: ButtonPresentationProps;
}

type Props = OwnProps &
    ButtonProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export const addToCartButtonStyles: ButtonPresentationProps = {};

const AddToCart = ({ extendedStyles, isTabletScreen, addWishListToCart, wishListId, ...otherProps }: Props) => {
    const [styles] = useState(() => mergeToNew(addToCartButtonStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const [currentStatus, setCurrentStatus] = useState(addToCartStatus.default);

    React.useEffect(() => {
        if (currentStatus === addToCartStatus.success) {
            setTimeout(() => {
                setCurrentStatus(addToCartStatus.default);
            }, 1000);
        }
    }, [currentStatus]);

    const clickAddToCartHandler = () => {
        setCurrentStatus(addToCartStatus.loading);
        wishListId &&
            addWishListToCart({
                apiParameter: { wishListId },
                onSuccess: onAddAllToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        setCurrentStatus(addToCartStatus.success);
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("Product Group Added to Cart"),
            messageType: "success",
        });
    };

    const iconConfig: IconConfig = {
        src:
            currentStatus === addToCartStatus.success
                ? addToCartStatus.success.icon
                : isTabletScreen && currentStatus === addToCartStatus.default
                  ? Cart
                  : "",
        position: "right",
    };

    return (
        <AddToCartButton
            extendedStyles={styles}
            currentStatus={currentStatus}
            setCurrentStatus={setCurrentStatus}
            iconConfig={iconConfig}
            data-status={currentStatus.status}
            onClick={clickAddToCartHandler}
            {...otherProps}
        >
            {!isTabletScreen ? translate("Add List to Cart") : ""}
        </AddToCartButton>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(withToaster(AddToCart));
